import { createWeb3Modal, defaultWagmiConfig } from '@web3modal/wagmi';
import { BLOCKCHAINS, PROVIDERS, getBlockchainByChainId, getChainsConfig, getEvmChainId } from '../providers';
import { reconnect, disconnect, signMessage, watchAccount, switchChain, switchAccount, getConnections } from '@wagmi/core';
import { ChangeAccCbArgs, Connector } from '../interfaces/wallet.interface';
import { Chain } from 'viem/chains';

const projectId = 'ed613db064bb88c7a349dee2e7112e21';

const metadata = {
  name: 'Ultrade',
  description: 'Ultrade Web3Modal',
  url: 'https://web3modal.com', //'https://dev.ultradedev.net', //TODO will it work on stage and testnet?
  icons: ['https://avatars.githubusercontent.com/u/37784886']
}


const chainsConfig = getChainsConfig();
const chains = Array.from(chainsConfig.values()) as [Chain, ...Chain[]];

export const wagmiConfig = defaultWagmiConfig({ chains, projectId, metadata });

const modal = createWeb3Modal({
  wagmiConfig: wagmiConfig,
  projectId,
  enableAnalytics: false,
  allowUnsupportedChain: true,
  //provider
  featuredWalletIds: ['c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96'],
});

enum ConnectionStatus {
  CONNECTED = 'CONNECTED',
  INITIATED = 'INITIATED',
  IN_PROGRESS = 'IN_PROGRESS',
  DISCONNECTED = 'DISCONNECTED',
  // SWITCHING_CHAIN = 'SWITCHING_CHAIN',
}

export const syncEvmEverySecond = () => {
  const intervalId = setInterval(() => {EVMConnector.getInstance().sync()}, 1000);
  return () => clearInterval(intervalId);
};

export class EVMConnector implements Connector {
  private static instance: EVMConnector;
  private constructor() {};
  private changeNetworkCb: (data: ChangeAccCbArgs) => void;
  private connectionStatus: ConnectionStatus = ConnectionStatus.DISCONNECTED;
  public chainId: number;
  private _isConnected: boolean;

  public address: string;

  public get isConnected(): boolean {
    return this._isConnected;
  }

  private getState() {
    const { state } = wagmiConfig;
    const { connections, current } = wagmiConfig.state
    const connection = connections.get(current!)
    const connector = connection?.connector;
    const accounts = connection?.accounts;
    console.log('EVM Wagmi state', {
      state: {...state},
      connections: [...connections],
      currentConn: {...connection},
      connector: {...connector},
    })
    return { connection, connector, accounts };
  }

  private unwatch?: () => void;
  private unsubModal?: () => void;
  private connectPromise?: Promise<BLOCKCHAINS>;
  private connectAttempt = 0;
  private isConnecting = false;
  private lastStatus: 'idle' | 'connecting' | 'connected' | 'disconnected' = 'idle';
  private unsubEvents?: () => void;
  
  public async connect(chain: BLOCKCHAINS, adapter: PROVIDERS): Promise<BLOCKCHAINS> {
    const desiredChainId = getEvmChainId(chain);
    if (this.isConnecting) return this.connectPromise!;
  
    this.isConnecting = true;
    this.connectAttempt += 1;
    const attemptId = this.connectAttempt;
  
    const { connector, accounts } = this.getState();
    if (connector && accounts?.length) await this.disconnect().catch(() => {});
  
    this.connectionStatus = ConnectionStatus.INITIATED;
  
    this.connectPromise = new Promise<BLOCKCHAINS>(async (resolve, reject) => {
      let resolved = false;
      let timeoutId: any;
    
      const cleanup = () => {
        this.unwatch?.();
        this.unwatch = undefined;
        this.unsubModal?.();
        this.unsubModal = undefined;
        clearTimeout(timeoutId);
        this.isConnecting = false;
        this.connectPromise = undefined;
        this.lastStatus = 'idle';
      };
    
      this.unwatch?.();
      this.unwatch = watchAccount(wagmiConfig, {
        onChange: async (e) => {
          if (attemptId !== this.connectAttempt) return;
          this.lastStatus = e.status as any;
      
          if (e.status === 'connecting') {
            this.connectionStatus = ConnectionStatus.IN_PROGRESS;
          }
      
          if (e.status === 'connected' && !resolved) {
            this.connectionStatus = ConnectionStatus.CONNECTED;
            this._isConnected = true;
            this.address = e.address!;
            this.chainId = e.chainId!;
            if (this.chainId !== desiredChainId) {
              try { await switchChain(wagmiConfig, { chainId: desiredChainId }); } catch {}
            }
            resolved = true;
            cleanup();
            return resolve(getBlockchainByChainId(this.chainId));
          }
      
          if (e.status === 'disconnected') {}
        }
      });
    
      this.unsubModal?.();
      this.unsubModal = modal.subscribeState(({ open }) => {
        if (attemptId !== this.connectAttempt || resolved) return;
      
        if (!open && this.lastStatus !== 'connected') {
          cleanup();
          reject(new Error('Modal closed by user'));
        }
      });
    
      timeoutId = setTimeout(() => {
        if (!resolved && attemptId === this.connectAttempt) {
          cleanup();
          reject(new Error('Connect timeout'));
        }
      }, 90_000);
    
      try {
        await modal.open({ view: 'Connect' });
      } catch (e) {
        cleanup();
        reject(e as any);
      }
    });
  
    return this.connectPromise;
  }
  
  public async sync() {
    if (this.isConnecting) return;
    await this.reconnect(BLOCKCHAINS.ARBITRUM_ONE, PROVIDERS.METAMASK).catch(() => {});
  }

  public async switchChain(chain: BLOCKCHAINS) {
    const oldChain = getBlockchainByChainId(this.chainId);
    const chainId = getEvmChainId(chain);
    console.log('evm switch chain: ', chain, chainId, "oldChain: ", this.chainId);

    if (this.chainId !== chainId) {
      // this.connectionStatus = ConnectionStatus.SWITCHING_CHAIN;
      await switchChain(wagmiConfig, { chainId })
      .then(() => {
        console.log('EVM: switch success. From: ', oldChain, ' To: ', chain);
      })
      .catch(e => {
        console.log('evm cannot switch chain: ', e);
        // this.connectionStatus = ConnectionStatus.CONNECTED;
        throw e;
      });
    }
    return chainId;
  }

  public async switchAccount(chain: BLOCKCHAINS, adapter: PROVIDERS) {    
    const connections = getConnections(wagmiConfig);
    const { accounts } = this.getState();
    if (accounts.length > 1) {
      const ind = accounts.indexOf(this.address);
      const newAddress = ind + 1 > accounts.length - 1 ? accounts[0] : accounts[ind + 1];
      this.address = newAddress;
    } else {
      await this.disconnect();
      await this.connect(chain, adapter);
      await switchAccount(wagmiConfig, { connector: connections[0]?.connector })
      .then(e => console.log("switch account result: ", e))
      .catch(e => console.log("Error on switch account: ", e))
    }
  }

  public setStateChangeCb(cb: (data: ChangeAccCbArgs) => void): () => void {
    this.changeNetworkCb = cb;
    return watchAccount(wagmiConfig, {onChange: (acc) => {
      console.log('evm account changed', { ...acc }, wagmiConfig);
      if (acc.status === 'connected') {
        this.address = acc.address
        this.chainId = acc.chainId
        this._isConnected = acc.isConnected
        this.connectionStatus = ConnectionStatus.CONNECTED
      }

      cb({
        address: this.address,
        chainId: this.chainId,
        isConnected: this.isConnected,
      });
    }});
  }

  public onAccountChange(cb: (data: ChangeAccCbArgs) => void): () => void {
    this.changeNetworkCb = cb;
    return watchAccount(wagmiConfig, {onChange: (acc) => {
      console.log('evm account changed', { ...acc }, wagmiConfig);
      if (acc.status === 'connected') {
        this.address = acc.address
        this.chainId = acc.chainId
        this._isConnected = acc.isConnected
      }

      cb({
        address: this.address,
        chainId: this.chainId,
        isConnected: this.isConnected,
      });
    }});
  }

  public async reconnect(chain: BLOCKCHAINS, adapter: PROVIDERS): Promise<BLOCKCHAINS> {
    console.log('evm reconnect');
    this.getState();

    const res = await reconnect(wagmiConfig);
    console.log("recconect account", res);
    const account = res && res[0];
    if (!account?.accounts[0]) {
      console.log('No account to reconnect', res)
      return;
    }

    this.changeNetworkCb({
      address: account.accounts[0],
      chainId: account.chainId,
      isConnected: Boolean(account.accounts[0]),
    });

    return getBlockchainByChainId(account.chainId);
  }

  public static getInstance(): EVMConnector {
    if (!EVMConnector.instance) {
      EVMConnector.instance = new EVMConnector();
    }
    return EVMConnector.instance;
  }

  public async disconnect(): Promise<void> {
    const { connector } = this.getState();
    if (!connector?.disconnect) {
      console.log('No actual connection to disconnect')
      return;
    }
    return new Promise(async (res, rej) => {
      const removeListener = watchAccount(wagmiConfig, {onChange: (e) => {
        console.log('onChange on disconnect', { ...e });
        if (e.status === 'disconnected' || !e.connector.disconnect) {
          this.connectionStatus = ConnectionStatus.DISCONNECTED;
          removeListener();
          this.address = null;
          this._isConnected = false;
          this.chainId = null;
          res();
        } else {
          rej();
        }
      }})
      await disconnect(wagmiConfig).catch((err) => console.log('Error evm disconnect', err));
    })
  }

  public signMessage(message: string, encoding?: BufferEncoding): Promise<string> {
      return signMessage(wagmiConfig, {
        account: this.address as `0x${string}`,
        message: encoding ? {raw: '0x'+message as `0x${string}`} : message,
      })
  }
}
